
import { useState, useEffect, createContext, useContext } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  isAdmin: () => Promise<boolean>;
  isVendor: () => Promise<boolean>;
  isSessionValid: () => boolean;
  clearInvalidSession: () => void;
  refreshSession: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        try {
          if (event === 'SIGNED_IN' && session?.user) {
            setUser(session.user);
            setSession(session);
            setLoading(false);
          } else if (event === 'SIGNED_OUT') {
            setUser(null);
            setSession(null);
            setLoading(false);
          } else if (event === 'TOKEN_REFRESHED' && session?.user) {
            setUser(session.user);
            setSession(session);
          }
        } catch (error) {
          console.error('Error in auth state change:', error);
          // Clear session on any error, especially refresh token errors
          if (error instanceof Error && error.message.includes('refresh')) {
            console.warn('Refresh token error detected, clearing session');
            clearInvalidSession();
          } else {
            setUser(null);
            setSession(null);
          }
          setLoading(false);
        }
      }
    );

    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.warn('Error getting initial session:', error);
          // Clear any invalid session data
          setUser(null);
          setSession(null);
        } else if (session?.user) {
          setUser(session.user);
          setSession(session);
        }
      } catch (error) {
        console.error('Error in getInitialSession:', error);
        setUser(null);
        setSession(null);
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    try {
      // First attempt server logout if we have a session
      if (session && isSessionValid()) {
        try {
          const { error } = await supabase.auth.signOut();
          if (error) {
            console.warn('Supabase sign out error:', error);
          }
        } catch (error) {
          console.warn('Supabase sign out error:', error);
        }
      }
      
      // Clear all local storage
      localStorage.removeItem('cart');
      localStorage.removeItem('checkout-progress');
      localStorage.removeItem('lastOrderTime');
      
      // Clear all Supabase-related storage
      try {
        localStorage.removeItem('supabase.auth.token');
        localStorage.removeItem('supabase.auth.expires_at');
        localStorage.removeItem('supabase.auth.refresh_token');
        localStorage.removeItem('supabase.auth.expires_in');
        localStorage.removeItem('supabase.auth.access_token');
        localStorage.removeItem('supabase.auth.user');
        localStorage.removeItem('supabase.auth.session');
        
        // Clear any other potential auth keys
        Object.keys(localStorage).forEach(key => {
          if (key.includes('supabase') || key.includes('auth')) {
            localStorage.removeItem(key);
          }
        });
      } catch (error) {
        console.warn('Error clearing auth storage:', error);
      }
      
      // Clear user state
      setUser(null);
      setSession(null);
      
    } catch (error) {
      console.error('Sign out error:', error);
      // Ensure user state is cleared even if there's an error
      setUser(null);
      setSession(null);
    }
  };

  const isAdmin = async () => {
    if (!user) return false;
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .single();
      
      return !error && !!data;
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  };

  const isVendor = async () => {
    if (!user) {
      return false;
    }
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'vendor')
        .maybeSingle();
      
      return !error && !!data;
    } catch (error) {
      console.error('Error checking vendor status:', error);
      return false;
    }
  };

  const isSessionValid = () => {
    if (!session) return false;
    // Check if session is not expired
    const now = Math.floor(Date.now() / 1000);
    return session.expires_at ? session.expires_at > now : true;
  };

  const clearInvalidSession = () => {
    console.log('Clearing invalid session');
    setUser(null);
    setSession(null);
    // Clear any stored auth data
    try {
      localStorage.removeItem('supabase.auth.token');
      localStorage.removeItem('supabase.auth.expires_at');
      localStorage.removeItem('supabase.auth.refresh_token');
    } catch (error) {
      console.warn('Error clearing auth storage:', error);
    }
  };

  const refreshSession = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.refreshSession();
      if (error) {
        console.warn('Session refresh failed:', error);
        clearInvalidSession();
        return false;
      }
      if (session) {
        setUser(session.user);
        setSession(session);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error refreshing session:', error);
      clearInvalidSession();
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      loading, 
      signUp,
      signIn,
      signOut,
      isAdmin,
      isVendor,
      isSessionValid,
      clearInvalidSession,
      refreshSession
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
